import classnames from 'classnames'
import ThemeConsumer from 'embed/components/ThemeConsumer'
import { css } from 'emotion'
import * as React from 'react'

const styles = require('./styles')

interface Props {
  selection: number
  options: Option[]
  handleChange: (value: number) => void
  className?: string
}

interface State {
  visible: boolean
  option: Option
}

interface Option {
  label: string
  value: number
}

const detectOutsideClick = (ref: { current: HTMLDivElement | null }, visible: boolean, handleToggle: () => void) => {
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (visible && ref.current && !ref.current.contains(event.target)) {
        handleToggle()
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [ref, visible, handleToggle])
}

export const TextDropdown: React.FC<Props> = ({ selection, options, handleChange, className }) => {
  const [state, setState] = React.useState<State>({
    visible: false,
    option: { label: '', value: 0 },
  })
  const wrapperRef = React.useRef<HTMLDivElement>(null)

  const handleToggle = React.useCallback(
    () => setState((prevState) => ({ ...prevState, visible: !prevState.visible })),
    [],
  )

  const handleSelect = (option: Option) => {
    if (option.value !== state.option.value) {
      handleChange(option.value)
      setState({ visible: false, option })
    } else {
      handleToggle()
    }
  }

  React.useEffect(() => {
    const foundOption = selection && options.find((item) => item.value === selection)
    const option = foundOption || options[0]
    setState({ visible: false, option })
  }, [selection, options])

  detectOutsideClick(wrapperRef, state.visible, handleToggle)

  return (
    <div ref={wrapperRef} className={styles.dropdownContainer}>
      <ThemeConsumer>
        {({ ctaStyles }) => (
          <div
            onClick={handleToggle}
            className={classnames(
              className,
              styles.noSelect,
              styles.optionSelection,
              css`
                color: ${ctaStyles?.backgroundColor};
              `,
            )}
          >
            <span>{state.option.label}</span>
            <div className={styles.caretWrapper}>
              <div
                className={classnames(
                  { [styles.caretDown]: !state.visible, [styles.caretUp]: state.visible },
                  css`
                    color: ${ctaStyles?.backgroundColor};
                  `,
                )}
              />
            </div>
          </div>
        )}
      </ThemeConsumer>

      {state.visible && (
        <div className={styles.optionsContainer}>
          {options.map((option) => (
            <div
              key={option.value}
              onClick={(e) => handleSelect(option)}
              className={classnames(styles.optionWrapper, {
                [styles.optionSelected]: option.value === state.option.value,
              })}
            >
              <p>{option.label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
